import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Customer } from '@api/Customers/Customer';
import * as Yup from 'yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { showAlertError } from '@utils/Alerts/Error';
import { hideLoader, showLoader } from '@utils/Loader';
import CustomerService from '@api/Customers/CustomerCtrl';
import WalletServ from '@api/Wallet/WalletServ';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Authorization from '@components/Utilities/Authorization';
import { showAlertMessage } from '@utils/Alerts/Message';

interface ModalAddPointsProps {
    modalAddPoints: OpenModalHookType;
    onAddPoints?: () => void;
    customer: Customer | undefined;
}

interface AddPoints {
    points: number;
}

const addPointsSchema = Yup.object({
    points: Yup.number().required('Puntos requeridos').min(1, 'Debe ser mayor a 0')
});

export const ModalAddPoints: React.FunctionComponent<ModalAddPointsProps> = (props) => {
    const { modalAddPoints, onAddPoints, customer } = props;
    const customerService = useMemo(() => new CustomerService(), []);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<AddPoints>({
        resolver: yupResolver(addPointsSchema)
    });

    // Resetear formulario cuando se cierra o cambia el cliente
    useEffect(() => {
        if (!modalAddPoints.modalOpen) {
            reset();
            setIsAuthorized(false);
        }
    }, [modalAddPoints.modalOpen, reset]);

    const handleClose = () => {
        reset();
        modalAddPoints.toggle();
    };

    const onAddPointsForm: SubmitHandler<AddPoints> = async (values) => {
        if (!customer?.uid) {
            showAlertError('No se ha seleccionado un cliente válido');
            return;
        }

        showLoader();
        try {
            await customerService.addPoints(customer.uid, values.points);
            // El saldo cambió: que el pase de Google Wallet lo refleje y el
            // cliente lo sepa. Fire-and-forget — nunca lanza (ver WalletServ).
            void WalletServ.refreshPass(customer.uid);
            showAlertMessage(`Se agregaron ${values.points} puntos a ${customer.profile?.name}`);
            if (onAddPoints) onAddPoints();
            handleClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalAddPoints.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-add-points-title"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-add-points-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CardGiftcardIcon sx={{ color: '#895BF1' }} />
                            <Typography variant="h6" fontWeight={600}>
                                Otorgar Puntos
                            </Typography>
                        </Stack>
                        {customer && (
                            <Typography variant="body2" color="textSecondary">
                                Cliente: <strong>{customer.profile?.name}</strong> ({customer.phone})
                            </Typography>
                        )}
                    </Stack>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ overflow: 'visible', mt: 1 }}>
                <form id="add-points-form" onSubmit={handleSubmit(onAddPointsForm)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Controller
                                name="points"
                                control={control}
                                defaultValue={0}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label="Cantidad de Puntos"
                                        placeholder="Ej. 100"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.points}
                                        helperText={errors.points?.message}
                                        autoFocus
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Authorization
                                onAuthorized={() => setIsAuthorized(true)}
                                onUnauthorized={() => setIsAuthorized(false)}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={!isAuthorized || !customer}
                    fullWidth
                    color="primary"
                    variant="contained"
                    type="submit"
                    form="add-points-form"
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
